import { Link } from "react-router-dom";

import { EmptyState, Loading } from "../components/StateViews";
import type { RouterOutputs } from "../trpc";
import { trpc } from "../trpc";

type Role = "learner" | "helper" | "admin";
type AdminUser = RouterOutputs["admin"]["users"][number];

export function AdminPage() {
  const me = trpc.session.me.useQuery();
  const users = trpc.admin.users.useQuery();

  if (me.isLoading) return <Loading label="Checking your access" />;

  if (me.data?.role !== "admin") {
    return (
      <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
        <EmptyState title="This area is for admins.">
          <Link to="/" className="text-terracotta hover:underline">
            Return home
          </Link>
        </EmptyState>
      </div>
    );
  }

  const pending = users.data?.filter((u) => u.role === null) ?? [];
  const members = users.data?.filter((u) => u.role !== null) ?? [];

  return (
    <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
      <p className="eyebrow">People &amp; access</p>
      <h1 className="mt-3 font-serif text-4xl text-ink">Who can do what</h1>
      <p className="mt-3 max-w-2xl leading-relaxed text-ink-soft">
        Anyone can sign in, but only approved members can capture, study and
        contribute. Approve people you know; a helper (translator) can also
        answer definition requests; an admin manages access.
      </p>

      {users.isLoading ? (
        <Loading label="Gathering people" />
      ) : (
        <>
          <section className="mt-10">
            <h2 className="eyebrow">
              Awaiting approval
              {pending.length > 0 ? ` (${pending.length})` : ""}
            </h2>
            {pending.length > 0 ? (
              <ul className="mt-3 divide-y divide-line rounded-2xl border border-terracotta/40 bg-terracotta-wash/40">
                {pending.map((user) => (
                  <UserRow key={user.id} user={user} pending />
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-ink-faint">
                No one is waiting — everyone who’s signed in has been sorted.
              </p>
            )}
          </section>

          <section className="mt-10">
            <h2 className="eyebrow">Members</h2>
            {members.length > 0 ? (
              <ul className="mt-3 divide-y divide-line rounded-2xl border border-line bg-paper-raised">
                {members.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-ink-faint">No members yet.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function UserRow({
  user,
  pending = false,
}: {
  user: AdminUser;
  pending?: boolean;
}) {
  const utils = trpc.useUtils();
  const setRole = trpc.admin.setRole.useMutation({
    onSuccess: () => utils.admin.users.invalidate(),
  });

  return (
    <li className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <p className="truncate font-serif text-lg text-ink">
          {user.displayName ?? user.email ?? user.id}
        </p>
        {user.displayName && user.email ? (
          <p className="truncate text-sm text-ink-faint">{user.email}</p>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-ink-faint">
          {setRole.isPending ? "Saving…" : setRole.isSuccess ? "Saved" : null}
        </span>
        {pending ? (
          <button
            type="button"
            disabled={setRole.isPending}
            onClick={() => setRole.mutate({ userId: user.id, role: "learner" })}
            className="rounded-full bg-ink px-4 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            Approve
          </button>
        ) : null}
        <select
          value={user.role ?? ""}
          disabled={setRole.isPending}
          onChange={(e) => {
            const v = e.target.value;
            setRole.mutate({
              userId: user.id,
              role: v === "" ? null : (v as Role),
            });
          }}
          className="rounded-xl border border-line-strong bg-paper-raised px-4 py-2 text-ink outline-none focus:border-terracotta/60 disabled:opacity-50"
        >
          <option value="">pending</option>
          <option value="learner">learner</option>
          <option value="helper">helper (translator)</option>
          <option value="admin">admin</option>
        </select>
      </div>
    </li>
  );
}
